import { useNavigate, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect } from 'react'
import { toast } from 'sonner'

import { transformRawSession } from '@/_providers/session-provider'

import { api } from '../_lib/api'
import { authClient } from '../_lib/auth-client'
import { useCompanyStore, useFacilityStore } from '../_stores'
import { LoginScene } from './scene'

const LoginPage = () => {
  const { data: session, status } = transformRawSession(authClient.useSession())
  const navigate = useNavigate()
  const searchParams = useSearch({ strict: false }) as { invitationId?: string }

  // Resolves a company (the given one, or the user's first company) and populates
  // the company/facility selection stores so company-scoped actions (e.g. creating
  // an application) work even before the user navigates into a company route.
  const hydrateSelectionStores = useCallback(async (companyId?: string) => {
    let company

    if (companyId) {
      const result = await api.companies.get(companyId)
      company = result.data
    } else {
      const result = await api.companies.list()
      company = result.data?.[0]
    }

    if (!company) {
      return null
    }

    console.info(`Company resolved`, {
      id: company.id,
      slug: company.slug,
    })

    useCompanyStore.getState().setCompany({
      id: company.id,
      slug: company.slug,
      name: company.name,
      cnpj: company.cnpj,
      legalName: company.legalName,
    })

    const facilitiesResult = await api.companies.facilities.list(company.id)

    const firstFacility = facilitiesResult.data?.[0]

    if (firstFacility) {
      useFacilityStore.getState().setCurrentFacility(firstFacility)
    }

    return company
  }, [])

  const handleTenantFlow = useCallback(
    async (companyId?: string) => {
      try {
        const company = await hydrateSelectionStores(companyId)

        if (!company) {
          navigate({ to: '/companies', replace: true })
          return
        }

        toast.success('Login successful!')
        navigate({ to: `/companies/${company.slug}/applications`, replace: true })
      } catch (error) {
        toast.error('Failed to complete login process')
      }
    },
    [navigate, hydrateSelectionStores],
  )

  const handleSessionSuccess = useCallback(async () => {
    if (!session) {
      return
    }

    const { user } = session
    const userType = user.userType ?? 'tenant'

    // If invitationId was passed via URL (from accept-invitation page), redirect back
    if (searchParams.invitationId) {
      navigate({
        to: `/login/accept-invitation/${searchParams.invitationId}`,
        replace: true,
      })
      return
    }

    // Check for pending invitations via BetterAuth
    try {
      const { data: invitations } = await authClient.organization.listUserInvitations()

      const pendingInvitation = invitations?.[0]
      if (pendingInvitation) {
        navigate({
          to: `/login/accept-invitation/${pendingInvitation.id}`,
          replace: true,
        })
        return
      }
    } catch (error) {
      console.error('[Login] Failed to check pending invitations:', error)
    }

    if (userType === 'staff') {
      // Pre-select the staff user's first company/facility so creating an
      // application works without first visiting a facility page (DEV-174).
      try {
        await hydrateSelectionStores()
      } catch (error) {
        console.error('[Login] Failed to pre-select company for staff:', error)
      }

      navigate({ to: '/companies', replace: true })
      return
    }

    await handleTenantFlow(user.defaultCompanyId ?? undefined)
  }, [session, handleTenantFlow, hydrateSelectionStores, navigate, searchParams.invitationId])

  useEffect(() => {
    if (status === 'authenticated') {
      handleSessionSuccess()
    }
  }, [status, handleSessionSuccess])

  if (status === 'loading') return <></>

  if (status !== 'authenticated') {
    return <LoginScene />
  }

  return <></>
}

export default LoginPage
